import { Store } from "idb-keyval";
import difference from "lodash/difference";
import isEqual from "lodash/isEqual";

// Utility function to add timeout to promises.
// Only time spent with the page visible counts towards the timeout: browsers
// throttle/suspend both timers and IndexedDB work in background tabs, so a
// wall-clock timer would fire spuriously on tab wake-up even though the
// operation completes normally (SEN-3527).
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, operation: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onVisibilityChange: (() => void) | undefined;

  const timeout = new Promise<T>((_, reject) => {
    let remainingMs = timeoutMs;
    let armedAt = 0;
    let armed = false;

    const arm = () => {
      if (armed) return;
      armed = true;
      armedAt = performance.now();
      timer = setTimeout(
        () => reject(new Error(`IndexedDB ${operation} timeout after ${timeoutMs}ms`)),
        remainingMs
      );
    };
    const disarm = () => {
      if (!armed) return;
      armed = false;
      clearTimeout(timer);
      remainingMs = Math.max(0, remainingMs - (performance.now() - armedAt));
    };

    // Workers have no `document`; they are never throttled by visibility.
    if (typeof document === "undefined") {
      arm();
      return;
    }

    onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        arm();
      } else {
        disarm();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (document.visibilityState === "visible") {
      arm();
    }
  });

  return Promise.race([promise, timeout]).finally(() => {
    clearTimeout(timer);
    if (onVisibilityChange) {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  });
};

// A simple addition to `idb-keyval`
export const getAll = <T>(store: Store): Promise<T[]> => {
  let req: IDBRequest<T[]>;
  return store
    ._withIDBStore("readonly", store => {
      req = store.getAll();
    })
    .then(() => req.result);
};

/**
 * Custom idb-keyval store to enable multiple stores per IndexedDB
 * database and auto-reconnect when DB has closed.
 *
 * Slightly different solution than
 * https://github.com/jakearchibald/idb-keyval/pull/50 for the
 * auto-reconnect feature.
 */
export class CustomStore {
  readonly _dbName: string;
  readonly _storeName: string;
  readonly _knownEntityStores: string[];
  readonly _timeoutMs: number;
  _openedDb: Promise<IDBDatabase> | null;

  // Type compat with idb-keyval `Store`
  readonly storeName: string = "UNUSED";
  readonly _dbp: Promise<IDBDatabase> = new Promise(() => {});

  constructor(
    dbName: string,
    storeName: string,
    knownEntityStores: string[],
    timeoutMs: number = 20000
  ) {
    this._dbName = dbName;
    this._storeName = storeName;
    this._knownEntityStores = knownEntityStores;
    this._timeoutMs = timeoutMs;
    this._openedDb = null;
  }

  _makeDbPromise(): Promise<IDBDatabase> {
    if (this._openedDb === null) {
      const dbOpenPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const openreq = indexedDB.open(this._dbName);
        openreq.onerror = () => reject(openreq.error);
        openreq.onsuccess = () => resolve(openreq.result);
      });

      this._openedDb = withTimeout(dbOpenPromise, this._timeoutMs, "database open").then(
        openreqResult => {
          const currentStoreNames = new Set(Array.from(openreqResult.objectStoreNames));
          const wantedStoreNames = new Set(this._knownEntityStores);

          // That's the main part that allows us to have multiple store per
          // database.
          // While opening the database we compare the stores we have
          // inside with the stores we want inside an create/drop the
          // diff.
          if (!isEqual(wantedStoreNames, currentStoreNames)) {
            // We will upgrade our DB to this "next version"
            const nextVersion = openreqResult.version + 1;

            // We need to reopen the database with `nextVersion` in order
            // to hook ourselves in `onupgradeneeded`.
            openreqResult.close();

            const upgradePromise = new Promise<IDBDatabase>((resolveNewDbp, rejectNewDbp) => {
              const newOpenreq = indexedDB.open(this._dbName, nextVersion);
              newOpenreq.onerror = () => rejectNewDbp(newOpenreq.error);
              newOpenreq.onsuccess = () => {
                // When the connection closes, we remove our ref to
                // `this._openDb`.
                newOpenreq.result.onclose = () => {
                  this._openedDb = null;
                };

                // Handle version change events when another tab tries to upgrade the database
                newOpenreq.result.onversionchange = () => {
                  this._openedDb = null;
                  newOpenreq.result.close();
                };

                resolveNewDbp(newOpenreq.result);
              };

              newOpenreq.onupgradeneeded = () => {
                difference(Array.from(currentStoreNames), Array.from(wantedStoreNames)).forEach(
                  name => newOpenreq.result.deleteObjectStore(name)
                );
                difference(Array.from(wantedStoreNames), Array.from(currentStoreNames)).forEach(
                  name => newOpenreq.result.createObjectStore(name)
                );
              };
            });

            return withTimeout(upgradePromise, this._timeoutMs, "database upgrade");
          }

          // Same as before, when the connection closes, we remove our
          // ref to `this._openedDb`.
          openreqResult.onclose = () => {
            this._openedDb = null;
          };

          // Handle version change events when another tab tries to upgrade the database
          openreqResult.onversionchange = () => {
            this._openedDb = null;
            openreqResult.close();
          };

          return openreqResult;
        }
      );
    }

    return this._openedDb;
  }

  _withIDBStore(
    type: IDBTransactionMode,
    callback: (store: IDBObjectStore) => void
  ): Promise<void> {
    return this._makeDbPromise().then(db => {
      const transactionPromise = new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(this._storeName, type);
        transaction.oncomplete = () => resolve();
        transaction.onabort = transaction.onerror = () => reject(transaction.error);
        callback(transaction.objectStore(this._storeName));
      });

      return withTimeout(transactionPromise, this._timeoutMs, "transaction");
    });
  }
}

export * from "idb-keyval";
